import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { CheckMenu } from '@/components/desktop/CheckMenu';
import { HistoryPanel } from '@/components/documents';
import { ActionDialog, type ActionField } from '@/components/hr/ActionDialog';
import { StatePill } from '@/components/hr/StatePill';
import { stateLabel } from '@/components/hr/states';
import { printPaySheet, printPayslips, printTable, type SheetRow } from '@/components/hrPrint';
import { ErrorNote, OkNote, SearchBar, errorMessage, money, shortDate, todayIso } from '@/components/ui';
import { formatNumber } from '@/format';
import {
  FREQUENCY_TEXT,
  PAY_METHOD_TEXT,
  WORKER_TYPE_TEXT,
  hrGet,
  periodLabel,
  useHrInvalidation,
  useHrLookups,
  type PaymentMethod,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { useShellNav } from '@/stores/shellNav';
import { PayslipPreview, type LineView, type PayslipDetail, type PayslipLine, type SlipRow } from './PayslipView';
import { dayCount, kindText } from './payrollShared';
import './payroll.css';

export type RunStage = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'POSTED' | 'REVERSED' | 'CANCELLED';

/** A pay run as the runs list returns it. */
export interface RunRow {
  id: string;
  documentNo: string;
  kind: 'REGULAR' | 'OFF_CYCLE';
  payGroup: 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';
  periodStart: string;
  periodEnd: string;
  farmId: string | null;
  farm: string;
  title: string | null;
  notes: string | null;
  stage: RunStage;
  headcount: number;
  totalGross?: string | null;
  totalDeduction?: string | null;
  totalNet?: string | null;
  totalEmployerCost?: string | null;
  calculatedAt: string | null;
  submittedAt: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  postedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  preparedByYou: boolean;
  version: number;
  createdAt: string;
  paidCount: number;
  heldCount: number;
  fullyPaid: boolean;
}

/** One pay run with every payslip, as GET /hr/payroll-runs/:id returns it. */
export interface RunDetail extends RunRow {
  outOfDate: string[];
  warningsCount: number;
  taxYear: string;
  taxYearMissing: boolean;
  methodSplit: { cash: string; bank: string; wallet: string };
  payslips: SlipRow[];
}

const OPEN_STAGES: RunStage[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'POSTED'];

type RunDialog =
  | { kind: 'bulkLine' | 'approve' | 'reject' | 'post' | 'reverse' }
  | { kind: 'markPaid' | 'markUnpaid' | 'print'; ids: string[] }
  | { kind: 'addLine' | 'hold' | 'remarks'; slip: SlipRow }
  | { kind: 'removeLine'; slip: SlipRow; lines: PayslipLine[] }
  | null;

interface DialogConfig {
  title: string;
  icon: IconKind;
  intro?: ReactNode;
  fields: ActionField[];
  initial?: Record<string, string>;
  submit: string;
  danger?: boolean;
  run: (v: Record<string, string>) => Promise<string>;
}

/** Fetches a run's payslips in full, a couple of hundred per request, so
 *  printing a big run costs a handful of requests rather than one a person. */
async function fetchPayslips(runId: string, ids: string[]): Promise<PayslipDetail[]> {
  const out: PayslipDetail[] = [];
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    const page = await hrGet<{ items: PayslipDetail[] }>(`/api/v1/hr/payroll-runs/${runId}/payslips`, {
      ids: chunk.join(','),
    });
    out.push(...page.items);
  }
  return out;
}

/**
 * The run workbench: every payslip in one pay run, the run's totals across
 * the top, and the whole workflow — calculate, send for approval, approve,
 * post, pay, print — on the toolbar. Choosing a payslip shows it in full
 * underneath, where one-off lines, holds and remarks are made.
 */
export function PayrollRun({
  runId,
  onBack,
  initialNote,
}: {
  runId: string;
  onBack: () => void;
  initialNote: string | null;
}) {
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const canUpdate = can('hr.payroll.update');
  const canPrint = can('hr.payroll.print');
  const lookups = useHrLookups(canUpdate);

  const [search, setSearch] = useState('');
  const [farmSel, setFarmSel] = useState<string[]>([]);
  const [onlyWarnings, setOnlyWarnings] = useState(false);
  const [onlyHeld, setOnlyHeld] = useState(false);
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('payslip');
  const [dialog, setDialog] = useState<RunDialog>(null);
  const [note, setNote] = useState<string | null>(initialNote);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const base = `/api/v1/hr/payroll-runs/${runId}`;
  const run = useQuery({
    queryKey: ['hr', 'payroll-runs', 'detail', runId],
    queryFn: () => hrGet<RunDetail>(base),
  });
  const detail = useQuery({
    queryKey: ['hr', 'payslips', 'detail', selectedKey],
    queryFn: () => hrGet<PayslipDetail>(`/api/v1/hr/payslips/${selectedKey}`),
    enabled: selectedKey !== null,
  });

  /** Runs one step and reports how it went; refreshes HR data unless told not to. */
  async function act(work: () => Promise<unknown>, message: string, refresh = true) {
    setBusy(true);
    setProblem(null);
    setNote(null);
    try {
      await work();
      if (refresh) invalidate();
      setNote(message);
    } catch (e) {
      setProblem(errorMessage(e, 'That did not work'));
    } finally {
      setBusy(false);
    }
  }

  const back = (
    <button type="button" className="btn btn--sm btn--ghost btn--withicon" onClick={onBack}>
      <span aria-hidden="true">←</span> Back to runs
    </button>
  );

  if (run.isPending) {
    return (
      <div className="workbench payrun">
        <div className="payrun__head">{back}</div>
        <p className="muted">Loading the pay run…</p>
      </div>
    );
  }
  if (run.isError) {
    return (
      <div className="workbench payrun">
        <div className="payrun__head">{back}</div>
        <ErrorNote message={run.error.message} />
      </div>
    );
  }

  const r = run.data;
  const slips = r.payslips;
  const stage = r.stage;
  const isDraft = stage === 'DRAFT';
  const isSubmitted = stage === 'SUBMITTED';
  const isApproved = stage === 'APPROVED';
  const isPosted = stage === 'POSTED';
  const stale = isDraft && r.outOfDate.length > 0;
  const sheetReady = isApproved || isPosted;
  const payVisible = r.totalNet !== undefined;
  const selected = slips.find((s) => s.id === selectedKey) ?? null;
  const checkedSlips = slips.filter((s) => checked.has(s.id));
  const scope = checkedSlips.length > 0 ? checkedSlips : slips;
  const toPay = scope.filter((s) => !s.onHold && !s.paidOn);
  const toUnpay = (checkedSlips.length > 0 ? checkedSlips : selected ? [selected] : []).filter((s) => s.paidOn);
  const held = slips.filter((s) => s.onHold).length;
  const period = periodLabel(r.periodStart, r.periodEnd);

  /** Busy overrides the state's own reason, so nothing is pressed twice. */
  function gate(ok: boolean, reason: string): Pick<DesktopAction, 'enabled' | 'reason'> {
    return busy ? { enabled: false, reason: 'Please wait — still working' } : { enabled: ok, reason };
  }

  const q = search.trim().toLowerCase();
  const rows = slips.filter(
    (s) =>
      (!q ||
        s.employeeCode.toLowerCase().includes(q) ||
        s.employeeName.toLowerCase().includes(q) ||
        (s.employeeNameUr ?? '').includes(search.trim())) &&
      (farmSel.length === 0 || farmSel.some((f) => (f === '-' ? s.farmCode === null : s.farmCode === f))) &&
      (!onlyWarnings || s.warnings.length > 0) &&
      (!onlyHeld || s.onHold) &&
      (!onlyUnpaid || !s.paidOn),
  );
  const farmCodes = [...new Set(slips.map((s) => s.farmCode).filter((c): c is string => c !== null))].sort();
  const farmOptions = [
    ...farmCodes.map((c) => ({ value: c, label: c })),
    ...(slips.some((s) => s.farmCode === null) ? [{ value: '-', label: 'Head office' }] : []),
  ];

  function calculate() {
    void act(() => api.post(`${base}/calculate`), 'Worked out again from the latest attendance, pay and deductions.');
  }

  async function printSlips(ids: string[], page: 'A4' | 'A5') {
    const full = await fetchPayslips(runId, ids);
    printPayslips(full, page);
    await api.post(`${base}/printed`, {
      what: full.length === 1 ? `Payslip ${full[0]!.employeeCode}` : `Payslips (${full.length})`,
    });
    return full.length === 1 ? 'Payslip sent to the printer.' : `${full.length} payslips sent to the printer.`;
  }

  function printSheet(method: PaymentMethod) {
    void act(
      async () => {
        const sheet = await hrGet<{
          documentNo: string;
          periodStart: string | null;
          periodEnd: string | null;
          method: PaymentMethod;
          items: SheetRow[];
        }>(`${base}/sheets/${method.toLowerCase()}`);
        printPaySheet(sheet);
        await api.post(`${base}/printed`, { what: `${PAY_METHOD_TEXT[method]} sheet` });
      },
      `${PAY_METHOD_TEXT[method]} sheet sent to the printer.`,
      false,
    );
  }

  function printRegister() {
    void act(
      async () => {
        printTable({
          title: `Payroll register — ${r.documentNo}`,
          subtitle: `${period} · ${kindText(r.kind)} · ${FREQUENCY_TEXT[r.payGroup]} · ${r.farm}${r.title ? ` · ${r.title}` : ''} · ${stateLabel(r.stage)}`,
          columns: [
            { key: 'employeeCode', header: 'Code' },
            { key: 'employeeName', header: 'Name' },
            { key: 'designationName', header: 'Designation' },
            { key: 'farmCode', header: 'Farm' },
            { key: 'workerType', header: 'Worker type' },
            { key: 'payableDays', header: 'Days paid', kind: 'number' },
            { key: 'absentDays', header: 'Absent', kind: 'number' },
            { key: 'overtimeHours', header: 'Overtime h', kind: 'number' },
            { key: 'grossSalary', header: 'Gross', kind: 'money' },
            { key: 'totalDeductions', header: 'Deductions', kind: 'money' },
            { key: 'netPayable', header: 'Net pay', kind: 'money' },
            { key: 'employerCost', header: 'Company cost', kind: 'money' },
            { key: 'paymentMethod', header: 'Paid by' },
            { key: 'paidOn', header: 'Paid on', kind: 'date' },
          ],
          rows: slips.map((s) => ({
            ...s,
            farmCode: s.farmCode ?? 'Head office',
            workerType: WORKER_TYPE_TEXT[s.workerType],
            paymentMethod: s.onHold ? `${PAY_METHOD_TEXT[s.paymentMethod]} (on hold)` : PAY_METHOD_TEXT[s.paymentMethod],
          })),
          landscape: true,
          signatures: ['Prepared by', 'Checked by', 'Approved by'],
        });
        await api.post(`${base}/printed`, { what: 'Payroll register' });
      },
      'Payroll register sent to the printer.',
      false,
    );
  }

  async function openRemoveLine(s: SlipRow) {
    setProblem(null);
    try {
      const d = await hrGet<PayslipDetail>(`/api/v1/hr/payslips/${s.id}`);
      const manual = d.lines.filter((l) => l.isManual);
      if (manual.length === 0) setProblem(`${s.employeeName}'s payslip has no lines typed in by hand.`);
      else setDialog({ kind: 'removeLine', slip: s, lines: manual });
    } catch (e) {
      setProblem(errorMessage(e, 'Could not load the payslip'));
    }
  }

  function removeLine(line: LineView) {
    if (!line.id) return;
    if (!window.confirm(`Remove "${line.label}" from this payslip? The payslip is worked out again.`)) return;
    void act(() => api.delete(`/api/v1/hr/payslip-lines/${line.id}`), `${line.label} removed.`);
  }

  const draftOnly = 'Only a draft pay run can be changed';

  function slipActions(s: SlipRow): DesktopAction[] {
    const openRun = OPEN_STAGES.includes(stage);
    return [
      {
        label: 'View',
        icon: 'payslip',
        shortcut: 'Enter',
        onSelect: () => {
          setSelectedKey(s.id);
          setTab('payslip');
        },
      },
      {
        label: 'Add a line',
        icon: 'add',
        onSelect: () => setDialog({ kind: 'addLine', slip: s }),
        allowed: canUpdate,
        ...gate(isDraft, draftOnly),
        separatorBefore: true,
      },
      {
        label: 'Remove a typed-in line',
        icon: 'remove',
        onSelect: () => void openRemoveLine(s),
        allowed: canUpdate,
        ...gate(isDraft, draftOnly),
      },
      s.onHold
        ? {
            label: 'Release pay',
            icon: 'hold',
            onSelect: () =>
              void act(() => api.post(`/api/v1/hr/payslips/${s.id}/release`), `${s.employeeName}'s pay released.`),
            allowed: canUpdate,
            ...gate(openRun, 'This pay run is closed'),
          }
        : {
            label: 'Hold pay',
            icon: 'hold',
            onSelect: () => setDialog({ kind: 'hold', slip: s }),
            allowed: canUpdate,
            ...gate(openRun && !s.paidOn, s.paidOn ? 'This pay has already been paid' : 'This pay run is closed'),
          },
      {
        label: 'Remarks',
        icon: 'edit',
        onSelect: () => setDialog({ kind: 'remarks', slip: s }),
        allowed: canUpdate,
        ...gate(isDraft, draftOnly),
      },
      {
        label: 'Print this payslip',
        icon: 'print',
        onSelect: () => void act(() => printSlips([s.id], 'A5'), 'Payslip sent to the printer.', false),
        allowed: canPrint,
        ...gate(true, ''),
        separatorBefore: true,
      },
      {
        label: 'Open the person',
        icon: 'employee',
        onSelect: () => useShellNav.getState().navigate('hr-emp', { recordId: s.employeeId }),
        allowed: can('hr.employee.read'),
      },
    ];
  }

  const workflow: DesktopAction[] = [
    {
      label: 'Calculate again',
      icon: 'calculate',
      primary: stale,
      onSelect: calculate,
      allowed: canUpdate,
      ...gate(isDraft, 'Only a draft pay run can be worked out again'),
    },
    {
      label: 'Add bonus or deduction',
      icon: 'add',
      onSelect: () => setDialog({ kind: 'bulkLine' }),
      allowed: canUpdate,
      ...gate(isDraft && slips.length > 0, isDraft ? 'There is no one on this pay run' : draftOnly),
    },
    {
      label: 'Send for approval',
      icon: 'submit',
      primary: isDraft && !stale,
      onSelect: () =>
        void act(
          () => api.post(`${base}/submit`),
          `${r.documentNo} sent for approval. Attendance for these days is now locked.`,
        ),
      allowed: canUpdate,
      ...gate(
        isDraft && !stale && r.headcount > 0,
        !isDraft
          ? 'Only a draft can be sent for approval'
          : stale
            ? 'Out of date — calculate again first'
            : 'There is no one on this pay run',
      ),
      separatorBefore: true,
    },
    {
      label: 'Take back',
      icon: 'reactivate',
      onSelect: () => void act(() => api.post(`${base}/recall`), `${r.documentNo} taken back to draft.`),
      allowed: canUpdate,
      ...gate(
        isSubmitted && r.preparedByYou,
        isSubmitted ? 'Only whoever prepared it can take it back' : 'Only a run waiting for approval can be taken back',
      ),
    },
    {
      label: 'Approve',
      icon: 'approve',
      primary: isSubmitted && !r.preparedByYou,
      onSelect: () => setDialog({ kind: 'approve' }),
      allowed: can('hr.payroll.approve'),
      ...gate(
        isSubmitted && !r.preparedByYou,
        isSubmitted
          ? 'You prepared this pay run, so someone else must approve it'
          : 'Only a run waiting for approval can be approved',
      ),
    },
    {
      label: 'Send back to draft',
      icon: 'reject',
      onSelect: () => setDialog({ kind: 'reject' }),
      allowed: can('hr.payroll.reject'),
      ...gate(
        (isSubmitted && !r.preparedByYou) || isApproved,
        isSubmitted
          ? 'You prepared this pay run; use Take back instead'
          : 'Only a run waiting for approval or posting can be sent back',
      ),
    },
    {
      label: 'Post',
      icon: 'post',
      primary: isApproved,
      onSelect: () => setDialog({ kind: 'post' }),
      allowed: can('hr.payroll.post'),
      ...gate(isApproved, 'It must be approved before it can be posted'),
      separatorBefore: true,
    },
    {
      label: 'Reverse',
      icon: 'reverse',
      danger: true,
      onSelect: () => setDialog({ kind: 'reverse' }),
      allowed: can('hr.payroll.reverse'),
      ...gate(
        isPosted && r.paidCount === 0,
        isPosted ? 'Some pay has been paid; put corrections in the next run' : 'Only a posted pay run can be reversed',
      ),
    },
  ];

  const hasMethod = (m: PaymentMethod) => slips.some((s) => s.paymentMethod === m && !s.onHold);
  const sheetAction = (m: PaymentMethod, label: string, icon: IconKind): DesktopAction => ({
    label,
    icon,
    onSelect: () => printSheet(m),
    allowed: canPrint,
    ...gate(
      sheetReady && hasMethod(m),
      sheetReady
        ? `No one on this run is paid by ${PAY_METHOD_TEXT[m].toLowerCase()}`
        : 'Payment sheets are ready once the pay run is approved',
    ),
  });

  const payBar: DesktopAction[] = [
    {
      label: 'Mark paid',
      icon: 'paid',
      primary: isPosted && toPay.length > 0,
      onSelect: () => setDialog({ kind: 'markPaid', ids: toPay.map((s) => s.id) }),
      allowed: canUpdate,
      ...gate(
        isPosted && toPay.length > 0,
        !isPosted
          ? 'Pay can be marked paid once the run is posted'
          : checkedSlips.length > 0
            ? 'The ticked payslips are already paid or on hold'
            : 'Everyone is paid or on hold',
      ),
    },
    {
      label: 'Undo paid',
      icon: 'clear',
      onSelect: () => setDialog({ kind: 'markUnpaid', ids: toUnpay.map((s) => s.id) }),
      allowed: canUpdate,
      ...gate(
        isPosted && toUnpay.length > 0,
        isPosted ? 'Tick or choose the payslips that were not really paid' : 'Only a posted pay run has paid payslips',
      ),
    },
    {
      label: 'Print payslips',
      icon: 'print',
      onSelect: () => setDialog({ kind: 'print', ids: scope.map((s) => s.id) }),
      allowed: canPrint,
      ...gate(slips.length > 0, 'There are no payslips to print'),
      separatorBefore: true,
    },
    sheetAction('CASH', 'Cash sheet', 'cash'),
    sheetAction('BANK', 'Bank sheet', 'bank'),
    sheetAction('WALLET', 'Wallet sheet', 'wallet'),
    {
      label: 'Print register',
      icon: 'report',
      onSelect: printRegister,
      allowed: canPrint,
      ...gate(slips.length > 0, 'There are no payslips'),
    },
  ];

  function toggleChecked(key: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const columns: Array<GridColumn<SlipRow>> = [
    { key: 'code', header: 'Code', width: 100, render: (s) => <span className="mono">{s.employeeCode}</span> },
    {
      key: 'name',
      header: 'Name',
      width: 200,
      render: (s) => (
        <span>
          {s.employeeName}
          {s.employeeNameUr && (
            <>
              {' '}
              <span className="urdu" lang="ur">
                {s.employeeNameUr}
              </span>
            </>
          )}
        </span>
      ),
    },
    { key: 'farm', header: 'Farm', width: 80, render: (s) => <span className="dim">{s.farmCode ?? 'Head office'}</span> },
    { key: 'type', header: 'Worker type', width: 110, render: (s) => WORKER_TYPE_TEXT[s.workerType] },
    { key: 'days', header: 'Days paid', width: 80, align: 'end', render: (s) => dayCount(s.payableDays) },
    {
      key: 'absent',
      header: 'Absent',
      width: 70,
      align: 'end',
      render: (s) => (Number(s.absentDays) > 0 ? dayCount(s.absentDays) : <span className="dim">0</span>),
    },
    {
      key: 'ot',
      header: 'Overtime h',
      width: 90,
      align: 'end',
      render: (s) => (Number(s.overtimeHours) > 0 ? formatNumber(s.overtimeHours) : <span className="dim">0</span>),
    },
    { key: 'gross', header: 'Gross', width: 110, align: 'end', render: (s) => money(s.grossSalary) },
    { key: 'ded', header: 'Deductions', width: 110, align: 'end', render: (s) => money(s.totalDeductions) },
    {
      key: 'net',
      header: 'Net',
      width: 110,
      align: 'end',
      render: (s) => <strong>{money(s.netPayable)}</strong>,
    },
    { key: 'method', header: 'Paid by', width: 110, render: (s) => PAY_METHOD_TEXT[s.paymentMethod] },
    {
      key: 'warnings',
      header: 'Warnings',
      width: 90,
      render: (s) =>
        s.warnings.length > 0 ? (
          <span className="withicon payrun__warn" title={s.warnings.join('\n')}>
            <AppIcon kind="attention" size={14} />
            {s.warnings.length}
          </span>
        ) : (
          <span className="dim">—</span>
        ),
    },
    {
      key: 'hold',
      header: 'On hold',
      width: 100,
      render: (s) =>
        s.onHold ? (
          <span title={s.holdReason ?? undefined}>
            <StatePill state="HELD" />
          </span>
        ) : (
          ''
        ),
    },
    {
      key: 'paid',
      header: 'Paid on',
      width: 110,
      render: (s) =>
        s.paidOn ? shortDate(s.paidOn) : isPosted && !s.onHold ? <StatePill state="UNPAID" /> : <span className="dim">—</span>,
    },
  ];

  const d = detail.data && detail.data.id === selectedKey ? detail.data : null;
  const tabs: DetailTab[] = selected
    ? [
        {
          id: 'payslip',
          label: 'Payslip',
          icon: 'payslip',
          content: d ? (
            <PayslipPreview slip={d} onRemoveLine={isDraft && canUpdate ? removeLine : undefined} />
          ) : detail.isError ? (
            <ErrorNote message={detail.error.message} />
          ) : (
            <p className="muted">Loading the payslip…</p>
          ),
        },
        { id: 'history', label: 'History', icon: 'history', content: <HistoryPanel path={`${base}/history`} /> },
      ]
    : [];

  const tile = (icon: IconKind, label: string, value: ReactNode) => (
    <span className="worktile">
      <span className="worktile__label withicon">
        <AppIcon kind={icon} size={14} />
        {label}
      </span>
      <strong>{value}</strong>
    </span>
  );

  function renderDialog(dg: NonNullable<RunDialog>) {
    const manualComponents = (lookups.data?.payComponents ?? []).filter(
      (c) => (c.kind === 'EARNING' || c.kind === 'DEDUCTION') && (c.systemCode === null || c.systemCode === 'ARREARS'),
    );
    const componentField: ActionField = {
      key: 'componentId',
      label: 'Pay item',
      type: 'select',
      required: true,
      options: manualComponents.map((c) => ({
        value: c.id,
        label: `${c.nameEn} — ${c.kind === 'EARNING' ? 'added to pay' : 'taken from pay'}`,
      })),
      hint: lookups.isPending ? 'Loading pay items…' : undefined,
    };
    const count = (n: number) => (n === 1 ? '1 payslip' : `${n} payslips`);

    let c: DialogConfig;
    switch (dg.kind) {
      case 'bulkLine': {
        const target = checkedSlips.length > 0 ? checkedSlips : slips;
        c = {
          title: 'Add a bonus or deduction',
          icon: 'add',
          intro:
            checkedSlips.length > 0
              ? `It goes on the ${count(target.length)} you ticked, and each is worked out again.`
              : `It goes on everyone in this run (${count(target.length)}), and each is worked out again. Tick people first to add it to only some.`,
          fields: [
            componentField,
            { key: 'amount', label: 'Amount for each person', type: 'number' },
            {
              key: 'percentOfPay',
              label: 'Or % of monthly pay',
              type: 'number',
              hint: 'e.g. 100 for one month’s pay. Daily-wage staff: daily rate × 26.',
            },
            { key: 'note', label: 'Note', placeholder: 'e.g. Eid ul Adha bonus' },
          ],
          submit: 'Add',
          run: async (v) => {
            const amount = v.amount?.trim() ?? '';
            const percent = v.percentOfPay?.trim() ?? '';
            if (!amount === !percent) throw new Error('Give either an amount or a % of pay — one of the two.');
            if (Number(amount || percent) <= 0) throw new Error('Enter a number above zero.');
            const result = await api.post<{ added: number }>(`${base}/lines`, {
              componentId: v.componentId,
              ...(amount ? { amount } : { percentOfPay: percent }),
              ...(checkedSlips.length > 0 ? { payslipIds: checkedSlips.map((s) => s.id) } : {}),
              ...(v.note?.trim() ? { note: v.note.trim() } : {}),
            });
            return `Added to ${count(result.added)}.`;
          },
        };
        break;
      }
      case 'approve':
        c = {
          title: `Approve ${r.documentNo}`,
          icon: 'approve',
          intro: `${r.headcount} people, net pay ${money(r.totalNet)}. Once approved, payment sheets can be printed and the Managing Director can post it.`,
          fields: [{ key: 'comment', label: 'Comment', type: 'textarea' }],
          submit: 'Approve',
          run: async (v) => {
            await api.post(`${base}/approve`, v.comment?.trim() ? { comment: v.comment.trim() } : {});
            return `${r.documentNo} approved.`;
          },
        };
        break;
      case 'reject':
        c = {
          title: `Send ${r.documentNo} back to draft`,
          icon: 'reject',
          intro: 'It goes back to whoever prepared it, with your reason, so it can be corrected and sent again.',
          fields: [{ key: 'comment', label: 'Why', type: 'textarea', required: true }],
          submit: 'Send back',
          danger: true,
          run: async (v) => {
            await api.post(`${base}/reject`, { comment: v.comment });
            return `${r.documentNo} sent back to draft.`;
          },
        };
        break;
      case 'post':
        c = {
          title: `Post ${r.documentNo}`,
          icon: 'post',
          intro: (
            <>
              Posting makes this pay run final. Advance and loan instalments on these payslips are recovered, back pay is
              settled, and labour cost is recorded against each farm. <strong>It cannot be changed afterwards</strong> —
              only reversed while nothing has been paid.
            </>
          ),
          fields: [],
          submit: 'Post',
          run: async () => {
            await api.post(`${base}/post`);
            return `${r.documentNo} posted. You can now pay people and mark them paid.`;
          },
        };
        break;
      case 'reverse':
        c = {
          title: `Reverse ${r.documentNo}`,
          icon: 'reverse',
          intro:
            'Advance recoveries and labour cost from this run are undone and the run is closed. Use this only for a mistake; nothing has been paid from it.',
          fields: [{ key: 'reason', label: 'Why', type: 'textarea', required: true }],
          submit: 'Reverse',
          danger: true,
          run: async (v) => {
            await api.post(`${base}/reverse`, { reason: v.reason });
            return `${r.documentNo} reversed.`;
          },
        };
        break;
      case 'markPaid': {
        const picked = slips.filter((s) => dg.ids.includes(s.id));
        const total = picked.reduce((t, s) => t + Number(s.netPayable ?? 0), 0);
        c = {
          title: 'Mark paid',
          icon: 'paid',
          intro: `${count(dg.ids.length)}${payVisible ? `, ${money(total.toFixed(2))} in all` : ''}. Anyone on hold or already paid is left out.`,
          fields: [
            { key: 'paidOn', label: 'Paid on', type: 'date', required: true },
            {
              key: 'method',
              label: 'How',
              type: 'select',
              required: true,
              options: [
                { value: 'OWN', label: 'Each person’s own way' },
                { value: 'CASH', label: PAY_METHOD_TEXT.CASH },
                { value: 'BANK', label: PAY_METHOD_TEXT.BANK },
                { value: 'WALLET', label: PAY_METHOD_TEXT.WALLET },
              ],
            },
            { key: 'reference', label: 'Voucher, cheque or bank batch number' },
          ],
          initial: { paidOn: todayIso(), method: 'OWN' },
          submit: 'Mark paid',
          run: async (v) => {
            const result = await api.post<{ paid: number }>(`${base}/mark-paid`, {
              payslipIds: dg.ids,
              paidOn: v.paidOn,
              ...(v.method && v.method !== 'OWN' ? { method: v.method } : {}),
              ...(v.reference?.trim() ? { reference: v.reference.trim() } : {}),
            });
            setChecked(new Set());
            return `${count(result.paid)} marked paid.`;
          },
        };
        break;
      }
      case 'markUnpaid':
        c = {
          title: 'Undo paid',
          icon: 'clear',
          intro: `${count(dg.ids.length)} will go back to "not paid yet". Use this only when pay was marked by mistake.`,
          fields: [{ key: 'reason', label: 'Why', type: 'textarea', required: true }],
          submit: 'Undo paid',
          danger: true,
          run: async (v) => {
            const result = await api.post<{ unpaid: number }>(`${base}/mark-unpaid`, { payslipIds: dg.ids, reason: v.reason });
            return `${count(result.unpaid)} back to not paid yet.`;
          },
        };
        break;
      case 'print':
        c = {
          title: 'Print payslips',
          icon: 'print',
          intro:
            checkedSlips.length > 0
              ? `The ${count(dg.ids.length)} you ticked.`
              : `Everyone in this run (${count(dg.ids.length)}). Tick people first to print only some.`,
          fields: [
            {
              key: 'page',
              label: 'Paper',
              type: 'select',
              required: true,
              options: [
                { value: 'A5', label: 'A5 — one payslip per page' },
                { value: 'A4', label: 'A4 — several to a page' },
              ],
            },
          ],
          initial: { page: 'A5' },
          submit: 'Print',
          run: (v) => printSlips(dg.ids, v.page === 'A4' ? 'A4' : 'A5'),
        };
        break;
      case 'addLine':
        c = {
          title: `Add a line — ${dg.slip.employeeName}`,
          icon: 'add',
          intro: 'A one-off bonus, arrears, fine or other deduction on this payslip. The payslip is worked out again.',
          fields: [
            componentField,
            { key: 'amount', label: 'Amount', type: 'number', required: true },
            { key: 'note', label: 'Note', placeholder: 'e.g. broken tray' },
          ],
          submit: 'Add line',
          run: async (v) => {
            if (!(Number(v.amount) > 0)) throw new Error('Enter an amount above zero.');
            await api.post(`/api/v1/hr/payslips/${dg.slip.id}/lines`, {
              componentId: v.componentId,
              amount: v.amount,
              ...(v.note?.trim() ? { note: v.note.trim() } : {}),
            });
            return `Line added to ${dg.slip.employeeName}'s payslip.`;
          },
        };
        break;
      case 'removeLine':
        c = {
          title: `Remove a line — ${dg.slip.employeeName}`,
          icon: 'remove',
          intro: 'Only lines typed in by hand can be removed. The rest are worked out by payroll.',
          fields: [
            {
              key: 'lineId',
              label: 'Line',
              type: 'select',
              required: true,
              options: dg.lines.map((l) => ({
                value: l.id,
                label: `${l.label} — ${money(l.payAmount)}${l.note ? ` (${l.note})` : ''}`,
              })),
            },
          ],
          initial: { lineId: dg.lines[0]?.id ?? '' },
          submit: 'Remove',
          danger: true,
          run: async (v) => {
            await api.delete(`/api/v1/hr/payslip-lines/${v.lineId}`);
            return 'Line removed.';
          },
        };
        break;
      case 'hold':
        c = {
          title: `Hold pay — ${dg.slip.employeeName}`,
          icon: 'hold',
          intro: 'Held pay is left off the payment sheets and cannot be marked paid until it is released.',
          fields: [{ key: 'reason', label: 'Why', type: 'textarea', required: true }],
          submit: 'Hold pay',
          run: async (v) => {
            await api.post(`/api/v1/hr/payslips/${dg.slip.id}/hold`, { reason: v.reason });
            return `${dg.slip.employeeName}'s pay is on hold.`;
          },
        };
        break;
      case 'remarks':
        c = {
          title: `Remarks — ${dg.slip.employeeName}`,
          icon: 'edit',
          intro: 'Printed at the bottom of the payslip.',
          fields: [{ key: 'remarks', label: 'Remarks', type: 'textarea' }],
          initial: { remarks: dg.slip.remarks ?? '' },
          submit: 'Save remarks',
          run: async (v) => {
            await api.patch(`/api/v1/hr/payslips/${dg.slip.id}/remarks`, { remarks: v.remarks?.trim() || null });
            return 'Remarks saved.';
          },
        };
        break;
    }

    const refresh = dg.kind !== 'print';
    return (
      <ActionDialog
        title={c.title}
        icon={c.icon}
        intro={c.intro}
        fields={c.fields}
        {...(c.initial ? { initial: c.initial } : {})}
        submitLabel={c.submit}
        danger={c.danger ?? false}
        onClose={() => setDialog(null)}
        onSubmit={async (v) => {
          const message = await c.run(v);
          if (refresh) invalidate();
          setProblem(null);
          setNote(message);
        }}
      />
    );
  }

  return (
    <div className="workbench payrun">
      <div className="payrun__head">
        {back}
        <h2 className="withicon">
          <AppIcon kind="payroll" />
          <span className="mono">{r.documentNo}</span>
          <span>
            {period} · {kindText(r.kind)} · {FREQUENCY_TEXT[r.payGroup]} · {r.farm}
            {r.title ? ` — ${r.title}` : ''}
          </span>
        </h2>
      </div>

      <div className="workbench__tiles">
        <span className="worktile">
          <span className="worktile__label">Stage</span>
          <StatePill state={r.fullyPaid ? 'PAID' : stage} label={r.fullyPaid ? 'Posted — fully paid' : stateLabel(stage)} />
        </span>
        {tile('people', 'People', r.headcount)}
        {tile('value', 'Gross', money(r.totalGross))}
        {tile('outbound', 'Deductions', money(r.totalDeduction))}
        {tile('payroll', 'Net pay', money(r.totalNet))}
        {tile('farm', 'Company cost', money(r.totalEmployerCost))}
        {tile(
          'cash',
          'Cash · Bank · Wallet',
          payVisible
            ? `${money(r.methodSplit.cash)} · ${money(r.methodSplit.bank)} · ${money(r.methodSplit.wallet)}`
            : '—',
        )}
        {tile('paid', 'Paid', `${r.paidCount} of ${r.headcount}`)}
        <button
          type="button"
          className={`worktile worktile--action${r.warningsCount > 0 ? ' worktile--warn' : ''}`}
          onClick={() => setOnlyWarnings((w) => !w)}
          aria-pressed={onlyWarnings}
          title="Show only payslips with warnings"
        >
          <span className="worktile__label withicon">
            <AppIcon kind="attention" size={14} />
            Warnings
          </span>
          <strong>{r.warningsCount}</strong>
        </button>
      </div>

      {stale && (
        <div className="payrun__alert" role="alert">
          <strong className="withicon">
            <AppIcon kind="attention" size={16} />
            Out of date — calculate again
          </strong>
          <p>Things have changed since this pay run was worked out:</p>
          <ul>
            {r.outOfDate.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          {canUpdate && (
            <button type="button" className="btn btn--sm btn--primary btn--withicon" onClick={calculate} disabled={busy}>
              <AppIcon kind="calculate" size={15} />
              Calculate again
            </button>
          )}
        </div>
      )}
      {r.taxYearMissing && (
        <div className="payrun__note" role="note">
          No tax year is set up for {r.taxYear}, so income tax and EOBI are not worked out on this run. Set it up in HR
          Setup, then calculate again.
          {can('hr.setup.read') && (
            <>
              {' '}
              <button type="button" className="linkbutton" onClick={() => useShellNav.getState().navigate('hr-setup')}>
                Open HR Setup
              </button>
            </>
          )}
        </div>
      )}
      {isDraft && r.decisionNote && <div className="payrun__note">{r.decisionNote}</div>}
      {stage === 'REVERSED' && r.reversalReason && (
        <div className="payrun__note">Reversed: {r.reversalReason}</div>
      )}

      <SplitView
        id="hr-payrun"
        direction="column"
        initial={380}
        firstLabel="payslip list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <SearchBar value={search} onChange={setSearch} placeholder="Search by code or name…" />
              {farmOptions.length > 1 && (
                <div className="field">
                  <span className="field__label">Farm</span>
                  <CheckMenu
                    wrapClassName="farmpicker"
                    menuClassName="farmpicker__menu"
                    menuLabel="Choose farms to show"
                    trigger={(open, toggleOpen) => (
                      <button
                        type="button"
                        className="farmpicker__trigger"
                        aria-haspopup="true"
                        aria-expanded={open}
                        onClick={toggleOpen}
                        aria-label="Filter slips by farm"
                      >
                        <span>
                          {farmSel.length === 0
                            ? `All farms (${farmOptions.length})`
                            : farmSel.length <= 2
                              ? farmSel.map((f) => farmOptions.find((o) => o.value === f)?.label ?? f).join(', ')
                              : `${farmSel.length} farms`}
                        </span>
                      </button>
                    )}
                  >
                    <label className="farmpicker__item">
                      <input type="checkbox" checked={farmSel.length === 0} onChange={() => setFarmSel([])} />
                      <span>All farms ({farmOptions.length})</span>
                    </label>
                    {farmOptions.map((o) => (
                      <label key={o.value} className="farmpicker__item">
                        <input
                          type="checkbox"
                          checked={farmSel.includes(o.value)}
                          onChange={() =>
                            setFarmSel((sel) =>
                              sel.includes(o.value) ? sel.filter((v) => v !== o.value) : [...sel, o.value],
                            )
                          }
                        />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </CheckMenu>
                </div>
              )}
              <label className="settings__toggle">
                <input type="checkbox" checked={onlyWarnings} onChange={(e) => setOnlyWarnings(e.target.checked)} />
                <span className="field__label">Only with warnings</span>
              </label>
              <label className="settings__toggle">
                <input type="checkbox" checked={onlyHeld} onChange={(e) => setOnlyHeld(e.target.checked)} />
                <span className="field__label">Only on hold</span>
              </label>
              <label className="settings__toggle">
                <input type="checkbox" checked={onlyUnpaid} onChange={(e) => setOnlyUnpaid(e.target.checked)} />
                <span className="field__label">Not paid yet</span>
              </label>
            </div>
            <ActionBar actions={workflow} />
            <ActionBar actions={payBar}>
              <span className="payrun__extra">
                {checked.size > 0 && (
                  <>
                    <span className="actionbar__count">{checked.size} ticked</span>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm btn--withicon"
                      onClick={() => setChecked(new Set())}
                    >
                      <AppIcon kind="clear" size={15} />
                      Clear ticks
                    </button>
                  </>
                )}
              </span>
            </ActionBar>
            {note && <OkNote message={note} />}
            {problem && <ErrorNote message={problem} />}
            <DataGrid
              id="hr-payslips"
              label="Payslips"
              columns={columns}
              rows={rows}
              rowKey={(s) => s.id}
              selectedKey={selectedKey}
              onSelect={(k) => setSelectedKey(k)}
              onOpen={(s) => {
                setSelectedKey(s.id);
                setTab('payslip');
              }}
              actions={slipActions}
              empty={
                slips.length === 0
                  ? 'No one is on this pay run. Check that people have approved pay and are in this pay group, then calculate again.'
                  : 'No payslips match these filters.'
              }
              selectedKeys={checked}
              onToggleSelect={toggleChecked}
              onToggleSelectAll={() =>
                setChecked((prev) => (rows.every((s) => prev.has(s.id)) ? new Set() : new Set(rows.map((s) => s.id))))
              }
              onSelectRange={(keys) => setChecked(new Set(keys))}
            />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a payslip to see it in full: days, earnings, deductions and net pay."
            title={
              selected && (
                <span className="withicon">
                  <AppIcon kind="payslip" size={14} />
                  <span className="mono">{selected.employeeCode}</span> {selected.employeeName}
                </span>
              )
            }
            {...(selected ? { actions: slipActions(selected) } : {})}
          />
        }
      />

      <StatusBar>
        <span>{stateLabel(stage)}</span>
        <span>{r.headcount} people</span>
        <span>Net pay: {money(r.totalNet)}</span>
        <span>
          Paid {r.paidCount} of {r.headcount}
        </span>
        {held > 0 && <span>{held} on hold</span>}
        {r.warningsCount > 0 && <span>{r.warningsCount} with warnings</span>}
        {rows.length !== slips.length && <span>{rows.length} shown</span>}
        <span>{selected ? `Chosen: ${selected.employeeCode} ${selected.employeeName}` : 'Nothing chosen'}</span>
      </StatusBar>

      {dialog && renderDialog(dialog)}
    </div>
  );
}
